import {
  DecisionPageLayout,
  DecisionSection,
  DecisionSubHeading,
  DecisionList,
  InlineRelatedLinks,
} from "@/components/DecisionPageLayout";
import { Link } from "@/lib/router-compat";

const seo = {
  title:
    "Federal Employee Depression Treatment | Neuro Wellness TMS Centers of America",
  description:
    "Federal employees with depression that hasn't responded to medication: learn about treatment options including TMS, SAINT, SPRAVATO, and more.",
  canonical: "/federal-employee-depression-treatment",
  keywords:
    "federal employee depression treatment, FEHB depression treatment, federal worker TMS, government employee mental health Florida",
};

const relatedLinks = [
  { title: "Federal Employee Hub", href: "/federal-employee-mental-health" },
  { title: "Federal Employee TMS", href: "/federal-employee-tms" },
  { title: "Federal Employee SAINT TMS", href: "/federal-employee-saint-tms" },
  { title: "Compare Treatments", href: "/compare-treatments" },
];

export default function FederalEmployeeDepressionTreatment() {
  return (
    <DecisionPageLayout
      seo={seo}
      badge="Depression Treatment"
      title="Depression Treatment for Federal Employees"
      subtitle="If you're a federal employee whose depression hasn't responded to medication, there are other options. Here's what may be available through your health plan."
    >
      <DecisionSection heading="Treatment-resistant depression">
        <p>
          If you've tried two or more antidepressants without adequate relief,
          there's a clinical term for what you're experiencing:
          treatment-resistant depression. It's not a judgment. It's a category
          that exists because this is common enough to need one — and because
          treatments were developed for it.
        </p>
        <p>
          You're not failing at treatment. The treatment has been failing to
          reach you, and there are other mechanisms.
        </p>
      </DecisionSection>

      <DecisionSection heading="Treatment options we offer">
        <DecisionList
          items={[
            "TMS (Transcranial Magnetic Stimulation) — FDA-cleared, non-invasive, outpatient",
            "SAINT accelerated TMS — FDA-cleared, five-day accelerated protocol with fMRI targeting",
            "SPRAVATO (esketamine) — FDA-approved nasal spray for treatment-resistant depression",
            "IV ketamine infusions — off-label but widely used for rapid symptom relief",
            "Ketamine-assisted psychotherapy (KAP) — combines ketamine with therapeutic processing",
            "PrTMS — personalized repetitive TMS",
            "Psychotherapy — CBT, DBT, EMDR, and other evidence-based approaches",
            "Medication management — psychiatric evaluation and ongoing medication oversight",
          ]}
        />
      </DecisionSection>

      <DecisionSection heading="How to decide">
        <p>
          The right treatment depends on your clinical history, what you've
          already tried, your preferences, and what your insurance plan covers.
          A consultation is the place to sort through this — not a website.
        </p>
        <p>
          We'll review your history, explain which treatments may be
          appropriate, and help you understand what your FEHB or PSHB plan may
          cover. If one treatment isn't the right fit, we'll tell you that and
          discuss what might be.
        </p>
      </DecisionSection>

      <DecisionSection heading="Your next step">
        <p>
          Call{" "}
          <a
            href="tel:9548279793"
            className="text-accent font-semibold hover:underline"
          >
            (954) 827-9793
          </a>{" "}
          or{" "}
          <Link
            to="/federal-employee-insurance-verification"
            className="text-accent hover:underline"
          >
            start the insurance verification process
          </Link>
          .
        </p>
      </DecisionSection>

      <InlineRelatedLinks heading="Related Pages" links={relatedLinks} />
    </DecisionPageLayout>
  );
}
